import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { Plus, Trash, UploadSimple } from "@phosphor-icons/react";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PhotoCropper from "./PhotoCropper";
import SymbolCheck from "./SymbolCheck";

const emptyTp = () => ({ trigger_type: "price", value: "", close_size_pct: "" });

const toLocalInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset());
  return dt.toISOString().slice(0, 16);
};

export default function TradeFormDialog({ open, onOpenChange, trade, prefill, onSaved }) {
  const isEdit = !!trade;
  const { config, user, setUser } = useAuth();
  const [form, setForm] = useState({});
  const [tps, setTps] = useState([emptyTp()]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [files, setFiles] = useState([]);
  const [cropQueue, setCropQueue] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exchanges, setExchanges] = useState([]);
  const [sendToEx, setSendToEx] = useState(false);
  const [orderConfirm, setOrderConfirm] = useState(false);
  const maxMb = config?.max_photo_mb || 10;

  const EX_LABELS = { bybit: "Bybit", binance: "Binance", okx: "OKX" };
  const defaultEx = user?.settings?.default_exchange || "bybit";
  const targetCfg = exchanges.find((e) => e.exchange === defaultEx);
  const spotUnsupportedOnEx = form.market_type === "spot" && defaultEx !== "okx";
  const canSend = !isEdit && config?.trading_mode === "live" && targetCfg?.trading_mode === "live" && !spotUnsupportedOnEx;

  useEffect(() => {
    if (open) {
      const src = trade || prefill || {};
      setForm({
        symbol: src.symbol || "",
        side: src.side || "long",
        market_type: src.market_type || "futures",
        entry_price: src.entry_price ?? "",
        quantity: src.quantity ?? "",
        stop_loss: src.stop_loss ?? "",
        reason: src.reason || "",
        trade_date: toLocalInput(trade?.trade_date),
        contract_value: src.quantity != null && src.entry_price != null
          ? +(src.quantity * src.entry_price).toFixed(2) : "",
      });
      setTps(
        src.take_profits?.length
          ? src.take_profits.map((tp) => ({
              trigger_type: tp.trigger_type,
              value: tp.value,
              close_size_pct: tp.close_size_pct ?? "",
            }))
          : [emptyTp()]
      );
      setFiles([]);
      setCropQueue([]);
      setTags(src.tags || []);
      setNewTag("");
      setSendToEx(false);
      setOrderConfirm(false);
      if (!isEdit) {
        api.get("/exchanges").then((r) => setExchanges(r.data)).catch(() => {});
      }
    }
  }, [open, trade, prefill, isEdit]);

  useEffect(() => {
    if (!canSend) { setSendToEx(false); setOrderConfirm(false); }
  }, [canSend]);

  const selectFiles = (list) => {
    const ok = [];
    for (const f of Array.from(list || [])) {
      if (f.size > maxMb * 1024 * 1024) {
        toast.error(`${f.name} exceeds the ${maxMb}MB limit`);
      } else {
        ok.push(f);
      }
    }
    if (ok.length) setCropQueue(ok);
  };

  const handleCropDone = (result) => {
    if (result) setFiles((f) => [...f, result]);
    setCropQueue((q) => q.slice(1));
  };

  const knownTags = user?.settings?.setup_tags || [];
  const allTags = [...new Set([...knownTags, ...tags])];
  const toggleTag = (t) => setTags((a) => (a.includes(t) ? a.filter((x) => x !== t) : [...a, t]));
  const addTag = async () => {
    const t = newTag.trim().toLowerCase().replace(/\s+/g, "-");
    if (!t) return;
    setNewTag("");
    if (!tags.includes(t)) setTags((a) => [...a, t]);
    if (!knownTags.includes(t)) {
      try {
        const { data } = await api.put("/settings", { setup_tags: [...knownTags, t] });
        setUser({ ...user, settings: data });
      } catch (e) {
        toast.error(`Tag saved on this trade, but adding it to your tag list failed: ${errDetail(e)}`);
      }
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setMarketType = (v) => setForm((f) => ({ ...f, market_type: v, side: v === "spot" ? "long" : f.side }));

  const setQty = (v) => setForm((f) => {
    const e = parseFloat(f.entry_price), q = parseFloat(v);
    return { ...f, quantity: v, contract_value: !isNaN(e) && !isNaN(q) ? +(q * e).toFixed(2) : "" };
  });
  const setContractValue = (v) => setForm((f) => {
    const e = parseFloat(f.entry_price), cv = parseFloat(v);
    return { ...f, contract_value: v, quantity: !isNaN(e) && e > 0 && !isNaN(cv) ? +(cv / e).toFixed(8) : f.quantity };
  });
  const setEntry = (v) => setForm((f) => {
    const e = parseFloat(v), q = parseFloat(f.quantity);
    return { ...f, entry_price: v, contract_value: !isNaN(e) && !isNaN(q) ? +(q * e).toFixed(2) : f.contract_value };
  });

  const setTp = (i, k, v) => setTps((arr) => arr.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));

  const previewPrice = (tp) => {
    const entry = parseFloat(form.entry_price);
    const val = parseFloat(tp.value);
    if (isNaN(entry) || isNaN(val)) return null;
    if (tp.trigger_type === "price") return val;
    const factor = form.side === "long" ? 1 + val / 100 : 1 - val / 100;
    return +(entry * factor).toFixed(6);
  };

  const submit = async () => {
    if (!form.symbol || !form.entry_price || !form.quantity) {
      toast.error("Symbol, entry price and quantity are required");
      return;
    }
    if (!/^[A-Za-z0-9]+\/[A-Za-z0-9]+$/.test(form.symbol.trim())) {
      toast.error("Symbol must be in BASE/QUOTE format, e.g. BTC/USDT");
      return;
    }
    if (form.market_type === "spot" && form.side === "short") {
      toast.error("Spot trades can't be short");
      return;
    }
    if (sendToEx && canSend && !orderConfirm) {
      toast.error("Confirm the live order, or turn off 'Send to exchange'");
      return;
    }
    setSaving(true);
    const payload = {
      symbol: form.symbol.toUpperCase().trim(),
      side: form.side,
      market_type: form.market_type || "futures",
      entry_price: parseFloat(form.entry_price),
      quantity: parseFloat(form.quantity),
      stop_loss: form.stop_loss === "" ? null : parseFloat(form.stop_loss),
      reason: form.reason,
      tags,
      trade_date: form.trade_date ? new Date(form.trade_date).toISOString() : null,
      take_profits: tps
        .filter((t) => t.value !== "" && !isNaN(parseFloat(t.value)))
        .map((t) => ({
          trigger_type: t.trigger_type,
          value: parseFloat(t.value),
          close_size_pct: t.close_size_pct === "" ? null : parseFloat(t.close_size_pct),
        })),
    };
    try {
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/trades/${trade.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/trades", payload);
        saved = data;
      }
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/trades/${saved.id}/photos`, fd);
      }
      if (!isEdit && sendToEx && canSend) {
        try {
          const { data: od } = await api.post(`/exchanges/${defaultEx}/place-order`, {
            symbol: payload.symbol,
            side: payload.side === "long" ? "Buy" : "Sell",
            order_type: "Limit",
            qty: payload.quantity,
            price: payload.entry_price,
            take_profit: saved.take_profits?.[0]?.target_price ?? null,
            stop_loss: payload.stop_loss,
            market_type: payload.market_type,
            confirm: true,
            trade_id: saved.id,
          });
          toast.success(`Order placed on ${EX_LABELS[defaultEx]} — ID ${od.order_id}`);
        } catch (orderErr) {
          toast.error(`Trade saved, but the ${EX_LABELS[defaultEx]} order failed: ${errDetail(orderErr)}`);
        }
      }
      toast.success(isEdit ? "Trade updated" : "Trade created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="trade-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Trade" : "New Trade"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="label-caps text-muted-foreground">Symbol</Label>
            <Input data-testid="trade-symbol-input" placeholder="BTC/USDT" value={form.symbol || ""}
              onChange={(e) => set("symbol", e.target.value)} className="font-mono mt-1" />
            <p className="text-[11px] text-muted-foreground mt-1">Base/quote, e.g. BTC/USDT, BTC/USDC, XRP/USDC.</p>
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Side</Label>
            <Select value={form.side} onValueChange={(v) => set("side", v)}>
              <SelectTrigger data-testid="trade-side-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="long">Long</SelectItem>
                <SelectItem value="short" disabled={form.market_type === "spot"}>Short</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Market</Label>
            <Select value={form.market_type || "futures"} onValueChange={setMarketType}>
              <SelectTrigger data-testid="trade-market-type-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="futures">Futures</SelectItem>
                <SelectItem value="spot">Spot</SelectItem>
              </SelectContent>
            </Select>
            {form.market_type === "spot" && (
              <p className="text-[11px] text-muted-foreground mt-1">Spot trading is currently only supported on OKX.</p>
            )}
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Entry Price</Label>
            <Input data-testid="trade-entry-input" type="number" step="any" value={form.entry_price ?? ""}
              onChange={(e) => setEntry(e.target.value)} className="font-mono mt-1" />
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Quantity (coins)</Label>
            <Input data-testid="trade-qty-input" type="number" step="any" value={form.quantity ?? ""}
              onChange={(e) => setQty(e.target.value)} className="font-mono mt-1" />
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Contract Value ($)</Label>
            <Input data-testid="trade-contract-value-input" type="number" step="any" value={form.contract_value ?? ""}
              onChange={(e) => setContractValue(e.target.value)} className="font-mono mt-1"
              placeholder="For example: 1000" />
            <p className="text-[11px] text-muted-foreground mt-1">Quantity × entry — fill in either one, the other is calculated.</p>
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Stop Loss</Label>
            <Input data-testid="trade-sl-input" type="number" step="any" value={form.stop_loss ?? ""}
              onChange={(e) => set("stop_loss", e.target.value)} className="font-mono mt-1" />
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Date &amp; Time</Label>
            <Input data-testid="trade-date-input" type="datetime-local" value={form.trade_date || ""}
              onChange={(e) => set("trade_date", e.target.value)} className="font-mono mt-1" />
            {!isEdit && (
              <p className="text-[11px] text-muted-foreground mt-1">Journal only — not sent to the exchange (orders are placed real-time).</p>
            )}
          </div>
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label className="label-caps text-muted-foreground">Take Profits</Label>
            <Button data-testid="add-tp-btn" variant="outline" size="sm" onClick={() => setTps((a) => [...a, emptyTp()])}>
              <Plus size={14} className="mr-1" /> Add TP
            </Button>
          </div>
          <div className="space-y-2">
            {tps.map((tp, i) => (
              <div key={i} className="grid grid-cols-[110px_1fr_1fr_90px_32px] gap-2 items-center border border-border p-2" data-testid={`tp-row-${i}`}>
                <Select value={tp.trigger_type} onValueChange={(v) => setTp(i, "trigger_type", v)}>
                  <SelectTrigger data-testid={`tp-type-select-${i}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price">Price</SelectItem>
                    <SelectItem value="percent">Move %</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid={`tp-value-input-${i}`} type="number" step="any" className="h-8 font-mono text-xs"
                  placeholder={tp.trigger_type === "price" ? "Target price" : "% move"}
                  value={tp.value} onChange={(e) => setTp(i, "value", e.target.value)} />
                <Input data-testid={`tp-size-input-${i}`} type="number" step="any" className="h-8 font-mono text-xs"
                  placeholder="Close size %" value={tp.close_size_pct}
                  onChange={(e) => setTp(i, "close_size_pct", e.target.value)} />
                <span className="text-xs font-mono text-muted-foreground" data-testid={`tp-preview-${i}`}>
                  {previewPrice(tp) != null ? `→ ${previewPrice(tp)}` : ""}
                </span>
                  <button data-testid={`tp-remove-btn-${i}`} onClick={() => setTps((a) => a.filter((_, idx) => idx !== i))}
                  className="text-muted-foreground hover:text-[hsl(var(--loss))] transition-colors duration-150">
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="label-caps text-muted-foreground">Reason / Rationale</Label>
          <Textarea data-testid="trade-reason-input" rows={3} className="mt-1"
            placeholder="Why are you taking this trade?"
            value={form.reason || ""} onChange={(e) => set("reason", e.target.value)} />
        </div>

        <div>
          <Label className="label-caps text-muted-foreground">Setup Tags</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="setup-tags-block">
            {allTags.map((t) => (
              <button key={t} type="button" data-testid={`setup-tag-${t}`} onClick={() => toggleTag(t)}
                className={`text-xs font-mono uppercase px-2 py-1 border transition-colors duration-150 ${
                  tags.includes(t)
                    ? "border-[hsl(var(--primary))] text-primary bg-[hsl(var(--primary))]/10"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}>
                {t}
              </button>
            ))}
            <div className="flex items-center gap-1">
              <Input data-testid="new-tag-input" value={newTag} placeholder="new tag…"
                className="h-7 w-32 text-xs font-mono"
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} />
              <Button type="button" data-testid="add-tag-btn" variant="outline" size="sm" className="h-7 px-2" onClick={addTag}>
                <Plus size={12} />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Tag the setup (e.g. breakout, support-bounce) — Analytics shows performance per tag.
          </p>
        </div>

        <div>
          <Label className="label-caps text-muted-foreground">Photos (charts, screenshots)</Label>
          <label className="mt-1 flex items-center gap-2 border border-dashed border-border px-3 py-2 cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">
            <UploadSimple size={16} />
            {files.length ? `${files.length} photo(s) attached` : "Choose images…"}
            <input data-testid="trade-photo-input" type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => { selectFiles(e.target.files); e.target.value = ""; }} />
          </label>
          <p className="text-xs text-muted-foreground mt-1">Max {maxMb}MB per photo. You can crop each photo before attaching.</p>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {files.map((f, i) => (
                <span key={i} data-testid={`pending-photo-${i}`}
                  className="flex items-center gap-1 text-xs font-mono border border-border px-2 py-1">
                  {f.name}
                  <button onClick={() => setFiles((a) => a.filter((_, idx) => idx !== i))}
                    className="text-muted-foreground hover:text-[hsl(var(--loss))] transition-colors duration-150">
                    <Trash size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {!isEdit && (
          <div className="border border-border p-3 space-y-2" data-testid="send-to-exchange-block">
            <div className="flex items-center justify-between">
              <span className="text-sm">
                Send to exchange <span className="font-mono text-muted-foreground">({EX_LABELS[defaultEx]})</span>
              </span>
              <Switch data-testid="send-to-exchange-switch" checked={sendToEx}
                onCheckedChange={(v) => { setSendToEx(v); if (!v) setOrderConfirm(false); }}
                disabled={!canSend} />
            </div>
            <SymbolCheck exchange={defaultEx} symbol={(form.symbol || "").toUpperCase().trim()}
              marketType={form.market_type || "futures"}
              disabled={!targetCfg} disabledHint={`${EX_LABELS[defaultEx]} is not configured — add API keys in Settings.`} />
            {!canSend && (
              <p className="text-xs text-muted-foreground" data-testid="send-to-exchange-hint">
                {!targetCfg
                  ? `${EX_LABELS[defaultEx]} is not configured — add API keys in Settings. You can change the default exchange there too.`
                  : spotUnsupportedOnEx
                    ? "Spot trading is only supported on OKX. Switch Market to Futures, or set OKX as your default exchange in Settings."
                    : config?.trading_mode !== "live"
                      ? "Global TRADING_MODE is readonly — orders cannot be sent."
                      : `${EX_LABELS[defaultEx]} is in read-only mode — enable live trading in Settings.`}
                {" "}The trade will be saved in the journal only.
              </p>
            )}
            {sendToEx && canSend && (
              <div className="space-y-2">
                <p className="text-xs text-loss">
                  Places a REAL Limit order at the entry price with your stop loss and first take profit. Real financial risk applies.
                </p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid="form-order-confirm-checkbox" checked={orderConfirm}
                    onCheckedChange={(v) => setOrderConfirm(!!v)} />
                  I understand and confirm this live order
                </label>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" data-testid="trade-form-cancel-btn" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="trade-form-submit-btn" onClick={submit} disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Trade"}
          </Button>
        </div>
      </DialogContent>
      <PhotoCropper file={cropQueue[0] || null} onDone={handleCropDone} />
    </Dialog>
  );
}
